import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { db } from "utils/db";
import { Stage, Layer, Line, Circle, Rect } from "react-konva";
import { FormModal } from "./FormModal";
import "components/shared/occurrence-modal.css";

const COLORS = [
  { label: "Azul",     value: "#035184" },
  { label: "Vermelho", value: "#dc2626" },
  { label: "Amarelo",  value: "#f59e0b" },
  { label: "Verde",    value: "#16a34a" },
];

const STROKE_WIDTH = 3;

export function ModalComponent({ show, closeModal, image, position, onReportAdded, variant }) {
  const emptyObject = {
    key: Math.random(),
    titulo: "",
    descricao: "",
    acao: "",
    severidade: ""
  };
  const [formSection, setFormSection] = useState(emptyObject);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const divRef = useRef(null);
  const stageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: Math.round(window.innerWidth * 0.7),
    height: Math.round(window.innerHeight * 0.7)
  });
  const [imageSrc, setImageSrc] = useState(null);

  const [tool, setTool] = useState("pen");
  const [strokeColor, setStrokeColor] = useState("#035184");
  const [lines, setLines] = useState([]);
  const [circles, setCircles] = useState([]);
  const [rects, setRects] = useState([]);
  const [previewShape, setPreviewShape] = useState(null);
  const isDrawing = useRef(false);
  const shapeStart = useRef(null);

  useEffect(() => {
    setImageLoaded(false);
    if (image?.img instanceof File) {
      const reader = new FileReader();
      reader.onload = e => setImageSrc(e.target.result);
      reader.readAsDataURL(image.img);
    } else if (image?.base64) {
      setImageSrc(image.base64);
    } else {
      setImageSrc(null);
    }
  }, [image]);

  const [formErrors, setFormErrors] = useState({});

  const handleAddToCart = async () => {
    const errors = {};
    if (!formSection.titulo.trim()) errors.titulo = "Título é obrigatório.";
    if (!formSection.severidade) errors.severidade = "Selecione uma severidade.";
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setIsLoading(true);
    try {
      const stage = stageRef.current;
      const dataURL = stage.toDataURL();
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = dimensions.width;
      tempCanvas.height = dimensions.height;
      const ctx = tempCanvas.getContext("2d");

      const backgroundImage = new Image();
      backgroundImage.src = imageSrc;
      backgroundImage.onload = () => {
        ctx.drawImage(backgroundImage, 0, 0, dimensions.width, dimensions.height);

        const stageImage = new Image();
        stageImage.src = dataURL;
        stageImage.onload = async () => {
          ctx.drawImage(stageImage, 0, 0, dimensions.width, dimensions.height);
          const combinedDataURL = tempCanvas.toDataURL();
          await db.images.add({
            img: combinedDataURL,
            key: formSection.key
          });
          dispatch({ type: "ADD_TO_REPORT", payload: { ...formSection } });
          if (onReportAdded && position) {
            onReportAdded(formSection.key, position);
          }
          setIsLoading(false);
          setFormSection(emptyObject);
          closeModal();
        };
      };
    } catch (error) {
    } finally {
      setIsLoading(false);
      setFormSection(emptyObject);
      closeModal();
      handleClearAll();
    }
  };

  const handleForm = (e, key) => {
    const value = e.target?.value ?? "";
    setFormSection(prev => ({ ...prev, [key]: value }));
    if (formErrors[key]) setFormErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const handleMouseDown = e => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (tool === "pen") {
      isDrawing.current = true;
      setLines(prev => [...prev, { points: [pos.x, pos.y], stroke: strokeColor }]);
    } else if (tool === "circle" || tool === "rect") {
      shapeStart.current = { x: pos.x, y: pos.y };
    }
  };

  const handleMouseMove = e => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (tool === "pen" && isDrawing.current && lines.length > 0) {
      const last = lines[lines.length - 1];
      last.points = last.points.concat([pos.x, pos.y]);
      setLines([...lines.slice(0, -1), last]);
    } else if ((tool === "circle" || tool === "rect") && shapeStart.current) {
      const start = shapeStart.current;
      if (tool === "circle") {
        const radius = Math.sqrt(
          Math.pow(pos.x - start.x, 2) + Math.pow(pos.y - start.y, 2)
        );
        setPreviewShape({ type: "circle", x: start.x, y: start.y, radius });
      } else {
        const x = Math.min(start.x, pos.x);
        const y = Math.min(start.y, pos.y);
        const width = Math.abs(pos.x - start.x);
        const height = Math.abs(pos.y - start.y);
        setPreviewShape({ type: "rect", x, y, width, height });
      }
    }
  };

  const handleMouseUp = () => {
    if (tool === "pen") {
      isDrawing.current = false;
    } else if ((tool === "circle" || tool === "rect") && shapeStart.current && previewShape) {
      if (previewShape.type === "circle" && previewShape.radius > 2) {
        setCircles(prev => [...prev, { x: previewShape.x, y: previewShape.y, radius: previewShape.radius, stroke: strokeColor }]);
      } else if (previewShape.type === "rect" && previewShape.width > 2 && previewShape.height > 2) {
        setRects(prev => [...prev, { x: previewShape.x, y: previewShape.y, width: previewShape.width, height: previewShape.height, stroke: strokeColor }]);
      }
      shapeStart.current = null;
      setPreviewShape(null);
    }
  };

  const handleImageLoad = () => setImageLoaded(true);

  const handleClearAll = () => {
    setLines([]);
    setCircles([]);
    setRects([]);
    setPreviewShape(null);
    shapeStart.current = null;
  };

  const handleUndo = () => {
    if (lines.length > 0) setLines(prev => prev.slice(0, -1));
    else if (circles.length > 0) setCircles(prev => prev.slice(0, -1));
    else if (rects.length > 0) setRects(prev => prev.slice(0, -1));
  };

  const hasDrawings = lines.length > 0 || circles.length > 0 || rects.length > 0;

  useEffect(() => {
    const updateDimensions = () => {
      if (imageLoaded && divRef.current) {
        const width = divRef.current.clientWidth;
        const height = divRef.current.clientHeight;
        setDimensions({ width, height });
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [imageLoaded]);

  const canRenderStage = show && imageSrc && imageLoaded && dimensions.width > 0 && dimensions.height > 0;

  if (!show) return null;

  return (
    <div className="occ-overlay">
      <div className="occ-modal">
        <div className="occ-modal__header">
          <div className="occ-modal__header-inner">
            <span className="occ-modal__accent">Ocorrência</span>
            <h2 className="occ-modal__title">Registro de Ocorrência</h2>
            <p className="occ-modal__subtitle">Anote na imagem e preencha os dados abaixo.</p>
          </div>
          <button
            type="button"
            className="occ-modal__close"
            onClick={closeModal}
            aria-label="Fechar"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="occ-modal__body">
          <div className="occ-modal__canvas-wrap">
            <div ref={divRef} className="occ-modal__canvas-area">
              <img
                src={imageSrc}
                alt="Snapshot para anotação"
                className="occ-modal__img"
                onLoad={handleImageLoad}
              />
              {canRenderStage && (
                <Stage
                  ref={stageRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  width={dimensions.width}
                  height={dimensions.height}
                  className={`occ-modal__stage occ-modal__stage--${tool}`}
                  style={{ position: "absolute", top: 0, left: 0, borderRadius: "10px" }}
                >
                  <Layer>
                    {lines.map((line, i) => (
                      <Line
                        key={`line-${i}`}
                        points={line.points}
                        stroke={line.stroke ?? "#035184"}
                        strokeWidth={STROKE_WIDTH}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                      />
                    ))}
                    {circles.map((c, i) => (
                      <Circle
                        key={`circle-${i}`}
                        x={c.x}
                        y={c.y}
                        radius={c.radius}
                        stroke={c.stroke ?? "#035184"}
                        strokeWidth={STROKE_WIDTH}
                      />
                    ))}
                    {rects.map((r, i) => (
                      <Rect
                        key={`rect-${i}`}
                        x={r.x}
                        y={r.y}
                        width={r.width}
                        height={r.height}
                        stroke={r.stroke ?? "#035184"}
                        strokeWidth={STROKE_WIDTH}
                      />
                    ))}
                    {previewShape && previewShape.type === "circle" && (
                      <Circle
                        x={previewShape.x}
                        y={previewShape.y}
                        radius={previewShape.radius}
                        stroke={strokeColor}
                        strokeWidth={STROKE_WIDTH}
                        listening={false}
                      />
                    )}
                    {previewShape && previewShape.type === "rect" && (
                      <Rect
                        x={previewShape.x}
                        y={previewShape.y}
                        width={previewShape.width}
                        height={previewShape.height}
                        stroke={strokeColor}
                        strokeWidth={STROKE_WIDTH}
                        listening={false}
                      />
                    )}
                  </Layer>
                </Stage>
              )}
            </div>

            <div className="occ-modal__tools">
              <span className="occ-modal__tools-label">Anotar:</span>
              <div className="occ-modal__tools-btns">
                <button
                  type="button"
                  className={`occ-modal__tool-btn ${tool === "pen" ? "active" : ""}`}
                  onClick={() => setTool("pen")}
                  title="Caneta (desenho livre)"
                >
                  <i className="fas fa-pencil-alt" />
                </button>
                <button
                  type="button"
                  className={`occ-modal__tool-btn ${tool === "circle" ? "active" : ""}`}
                  onClick={() => setTool("circle")}
                  title="Círculo"
                >
                  <i className="fas fa-circle" />
                </button>
                <button
                  type="button"
                  className={`occ-modal__tool-btn ${tool === "rect" ? "active" : ""}`}
                  onClick={() => setTool("rect")}
                  title="Retângulo"
                >
                  <i className="fas fa-square" />
                </button>
              </div>
              <div className="occ-modal__tools-color">
                <span className="occ-modal__tools-label">Cor:</span>
                <div className="occ-modal__color-select-wrap">
                  <span
                    className="occ-modal__color-swatch-preview"
                    style={{ backgroundColor: strokeColor }}
                  />
                  <select
                    className="occ-modal__color-select"
                    value={strokeColor}
                    onChange={e => setStrokeColor(e.target.value)}
                  >
                    {COLORS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="occ-modal__tools-actions">
                <button
                  type="button"
                  className="occ-modal__tool-btn"
                  onClick={handleUndo}
                  disabled={!hasDrawings}
                  title="Desfazer"
                >
                  <i className="fas fa-undo" />
                </button>
                <button
                  type="button"
                  className="occ-modal__tool-btn occ-modal__tool-btn--danger"
                  onClick={handleClearAll}
                  disabled={!hasDrawings}
                  title="Apagar todas as anotações"
                >
                  <i className="fas fa-trash-alt" />
                </button>
              </div>
            </div>
          </div>

          <div className="occ-modal__form-wrap">
            <FormModal formSection={formSection} handle={handleForm} formErrors={formErrors} />
          </div>
        </div>

        <div className="occ-modal__footer">
          {isLoading ? (
            <div className="spinner-border text-primary" role="status" />
          ) : (
            <button
              type="button"
              onClick={handleAddToCart}
              className="occ-modal__submit"
            >
              Adicionar ao Relatório
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
